import {BaseElement, BASIS} from "/@back@/commons/basis.js";
import {BaseAreaViewAsync, IView, OBaseAreaViewInit} from "/@lib@/commons/views.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {IWspUiEnv, JWspUriChangeMsg, Wsp} from "/@lib@/wsp/wsp.js";
import {ESrcField, JSrcFieldsTree, srcRef, srcUri} from "/@lib@/wsp/src.js";
import {ISpaceTreeCtx, ISpaceTreeReq, SpaceTree, SpaceTreeDataSearch} from "/@back@/wsp/views/spaceTree.js";
import {ILastDatasBuilder, JLastDatas} from "/@lib@/commons/lastDatas.js";
import {AccelKeyMgr, ACTION, IAccelKeyMgrPointer, IAction} from "/@lib@/commons/actions.js";
import {InfoCurrentItem, InfoHighlighItemSgn, InfoReqCurrentItem, IShortDescCtx, ITEM} from "/@lib@/wsp/item.js";
import {GridColTreeDef} from "/@back@/commons/widgets/tree.js";
import {GridColDef, IGrid, IGridDataRow, IGridLineDrawer} from "/@back@/commons/widgets/grid-core.js";
import {IInfo, IInfoBroker, IInfoConsumer} from "/@lib@/commons/infos.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {ISearchCrit, SearchItems, SearchOr, SearchStatus} from "/@lib@/wsp/search.js";
import {IActionable, IContextMenuActions, IFocusActionables} from "/@back@/commons/actionables.js";
import {Grid} from "/@back@/commons/widgets/grid-tags.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {CellBuilderSrcIconCodeTitle, redrawSrcLine} from "/@back@/wsp/widgets/srcGridColumns.js";
import {GridDataHolderJsonArray} from "/@back@/commons/widgets/grid-libs.js";
import {ActionBtn, OButtonInit} from "/@back@/commons/widgets/buttons.js";
import {LANG} from "/@lib@/commons/lang.js";
import {IWspXmlHouse, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";

/**
 *
 */
export interface Navigator extends BaseElement, IView {
	initialize(init: ONavigatorInit): this
}

export interface ONavigatorInit extends OBaseAreaViewInit<IRegPointer<IWspUiEnv>> {

	/**
	 * Construction du gestionnaire des facettes, responsable de construire la requete au server.
	 * Par défaut, un NavigatorFacetText générique sera construit.
	 */
	buildFacets?: (nav: Navigator, fields: ESrcField[]) => Promise<INavigatorFacets>

	/** Redéfinition de la colonne primaire. */
	primaryCol?: GridColTreeDef

	/** Colonnes secondaires à ajouter du grid. */
	secondaryCols?: GridColDef[]

	/** Actions associées aux items sélectionnés (toolbar et menu contextuel). */
	actions?: IAction<IShortDescCtx>[];

	/** Action excutée au double-click ou sur les frappes entrée ou espace. */
	defaultAction?: IAction<ISpaceTreeCtx>

	/** Racourcis claviers à capter sur la sel courante. */
	accelKeyMgr?: AccelKeyMgr<ISpaceTreeCtx>;

	/** Réagira aux infos issues de ce broker (item courant, highlight d'items selon signature) */
	itemHandlingReact?: IInfoBroker

	/** Contenu de la zone principale lorsque la liste est vide. */
	emptyTree?: (navigator: Navigator) => Node

	/** LastDatas transférés à ONavigatorInit.buildFacets() */
	lastDatas?: JLastDatas
}

/** Gestionnaire des facettes et constructeur de la requete. */
export interface INavigatorFacets extends ISpaceTreeReq, ILastDatasBuilder {

	/**
	 * Construction graphique des facettes
	 * - Les facettes doivent être ajoutées à nav.facetRoot
	 * - La construction des facettes DOIT intégrer nav.navigatorBtns (1, voire 2 boutons) dans nav.facetRoot
	 */
	buildUiFacets: (nav: Navigator, lastDatas: JLastDatas | null) => void
}

export class Navigator extends BaseAreaViewAsync<IRegPointer<IWspUiEnv>> implements IInfoConsumer, IGridLineDrawer, ILastDatasBuilder, IFocusActionables<IShortDescCtx>, IAccelKeyMgrPointer<IShortDescCtx> {

	reg: IReg<IWspUiEnv>;
	wsp: Wsp;

	facets: INavigatorFacets;

	facetRoot: HTMLElement;
	navigatorBtns: HTMLElement;
	grid: Grid;
	datas: NavigatorDatas;

	infoBroker: IInfoBroker;

	accelKeyMgr?: AccelKeyMgr<IShortDescCtx>;

	/** Sélection en cours */
	shortDescs: JSrcFieldsTree[] = [];

	get emitter() {return this.grid} //on pointe le noeud focusable.
	get me() {return this} // Astuce pour retrouver le SpaceTree quand l'actionContext a été surchargé.

	/** boutons à afficher dans la focusToolbar. */
	focusActionables?: IActionable<IShortDescCtx>[];

	/** Actions associées aux items sélectionnés (mémoire pour le menuContextuel). */
	actions?: IAction<IShortDescCtx>[];

	emptyTree: (navigator: Navigator) => Node;

	setCurrentItem(srcUri: srcUri | null) {
		this._currentSrcUri = srcUri;
		this.grid.invalidateRows();
		//A voir: != de explorateur, veut-on bouger ce scroll, pas top si on est en train de passer les items en revue ?
		//if (this._currentSrcUri) this.ensureRowVisible(this._currentSrcUri);
	}

	async selectSrcUri(srcUri: srcUri): Promise<boolean> {
		//if (!await this.datas.fetchSrcUri(srcUri)) return false; attention, potentiellement couteux
		const rowKey = this.datas.findRowKeyBySrcUri(srcUri);
		if (rowKey) this.grid.setSelectedRows(this.datas.getOffset(rowKey));
		return rowKey != null;
	}

	async selectSrcUris(srcUris: srcUri[]): Promise<void> {
		//await this.datas.fetchSrcUris(srcUris); attention, potentiellement couteux
		this.grid.setSelectedRows(
			srcUris.map((srcUri) => this.datas.findRowKeyBySrcUri(srcUri))
				.filter((rowKey) => rowKey != null)
				.map((rowKey) => this.datas.getOffset(rowKey))
		);
	}

	async ensureRowVisible(srcUri: srcUri): Promise<boolean> {
		//if (!await this.datas.fetchSrcUri(srcUri)) return false; Non, potentiellement trop couteux.
		const rowKey = this.datas.findRowKeyBySrcUri(srcUri);
		if (rowKey) this.grid.ensureRowVisible(this.datas.getOffset(rowKey));
		return rowKey != null;
	}

	doSearch(): Promise<void> {
		return this.datas.fetchSearchStart(true);
	}

	protected async _initialize(init: ONavigatorInit): Promise<void> {
		super._initialize(init); //init : reg, area, areaContext
		this.wsp = this.reg.env.wsp;
		this.infoBroker = init.itemHandlingReact;
		this.emptyTree = init.emptyTree;

		const fields = this.wsp.getShortDescFields();
		this.facets = init.buildFacets ? await init.buildFacets(this, fields) : new NavigatorFacetText(fields);
		this.datas = new NavigatorDatas(this.reg).initNavDatas(this);

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this.facetRoot = sr.appendChild(<div id="facetRoot"/>);

		this.navigatorBtns = <c-button î={{reg: this.reg, title: "Rafraichir la liste", icon: "/@skin@/commons/icons/refresh.svg", uiContext: "bar"} as OButtonInit} onclick={(ev: MouseEvent) => {this.doSearch()}}/>;

		const colDefs: GridColDef[] = [init.primaryCol || new GridColTreeDef('srcTree')
			.setFlex('1rem', 1, 1).setMinWidth('55px')
			.setCellBuilder(new CellBuilderSrcIconCodeTitle(this.reg, this.wsp.wspMetaUi, false, this.wsp.srcUriItemsSortFn))];
		if (init.secondaryCols) colDefs.push(...init.secondaryCols);

		this.grid = sr.appendChild(new Grid().initialize({
			selType: 'multi',
			columnDefs: colDefs,
			dataHolder: this.datas,
			hideHeaders: true,
			lineDrawer: this.infoBroker ? this : null,
			skinOver: 'wsp-navigator/grid',
			skinScroll: 'scroll/small',
			noResizableCol: true,
			//autoSelOnFocus: 'first', NON pb pour executer des actions (Créer un item, ...) sur la racine.
			emptyBody: (): Node => {
				if (this.datas.lastError === undefined) {
					return <c-msg label="Chargement..." level="info"/>
				} else if (this.datas.lastError === null) {
					return this.emptyTree ? this.emptyTree(this) : <c-msg label="Aucun item ne correspond à vos critères" level="info"/>
				} else {
					return <c-msg label="Accès au serveur impossible" level="error"/>
				}
			},
			defaultAction: init.defaultAction,
			defaultActionCtx: this
		}));

		if (init.secondaryCols) DOM.setAttrBool(this.grid, "data-has-secondary-cols", true);

		//Impl IContextMenuActionsPointer<IShortDescCtx> sur le grid
		Object.defineProperty(this.grid, "ctxMenuActions", {
			get: (): IContextMenuActions<IShortDescCtx> | null => {return {actions: this.actions, actionContext: this, rect: this.grid.getSelRect()}}
		});

		this.grid.addEventListener('grid-select', function (this: IGrid, ev: Event) {
			const spaceTree = DOMSH.findHost<SpaceTree>(this);
			spaceTree.shortDescs = (this.dataHolder as GridDataHolderJsonArray<JSrcFieldsTree>).getSelectedDatas();
			//console.log("spaceTree.shortDescs::::", spaceTree.shortDescs);
			if (this.matches(':focus')) this.dispatchEvent(new CustomEvent<IFocusActionables<IShortDescCtx>>('c-focus-actions', {detail: spaceTree, bubbles: true, composed: true}));
		});

		this.grid.addEventListener('focus', function (this: IGrid, ev: Event) {
			this.dispatchEvent(new CustomEvent<IFocusActionables<IShortDescCtx>>('c-focus-actions', {detail: DOMSH.findHost(this) as SpaceTree, bubbles: true, composed: true}));
		});

		this.grid.linesNode.setAttribute("draggable", "true");
		this.grid.linesNode.addEventListener('dragstart', function (ev: DragEvent) {
			//console.log(ev);
			ITEM.setShortDescTransferToDragSession(DOMSH.findHost<SpaceTree>(DOMSH.findHost<IGrid>(this)), ev, 'build');
		});
		this.grid.linesNode.addEventListener('dragend', ITEM.resetShortDescTransferToDragSession);

		if (init.accelKeyMgr) {
			this.accelKeyMgr = init.accelKeyMgr;
			this.grid.addEventListener('keydown', (ev: KeyboardEvent) => {
				this.accelKeyMgr.handleKeyboardEvent(ev, this);
			});
		}

		let actions = init.actions;
		if (!actions && this.hasAttribute("actions")) actions = this.reg.mergeLists(...BASIS.extractAttr(this, 'actions').split(' '));
		if (actions) {
			actions = ACTION.injectSepByGroup(actions, this.reg.getPref("groupOrder.wsp.shortDesc", ""), this);
			this.actions = actions;
			this.focusActionables = ActionBtn.buildButtons(actions, this, 'bar');
		}

		//
		this.reg.env.place?.eventsMgr.on("wspUriChange", (this._onUriChangeLstn = this.onUriChange.bind(this)));

		this.facets.buildUiFacets(this, init.lastDatas ? init.lastDatas[init.area.getId()] : null);
		this.datas.fetchSearchStart();
	}

	onUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | IWspXmlHouse | 'local' | 'server') {
		this.datas.onUriChange(msg, from);
	}

	protected _onUriChangeLstn: (msg: JWspUriChangeMsg, from: WspsLivePlace | IWspXmlHouse | 'local' | 'server') => void;

	connectedCallback() {
		super.connectedCallback();
		if (this.infoBroker) {
			this._highlightSgn = null;
			this._assigned = null;
			const req = new InfoReqCurrentItem();
			this.infoBroker.dispatchInfo(req, this);
			this.setCurrentItem(req.srcUri || null);
			this.infoBroker.addConsumer(this);
		}
	}

	diconnectedCallback() {
		if (this.infoBroker) this.infoBroker.removeConsumer(this);
	}

	onViewHidden(closed: boolean) {
		if (closed) this.reg.env.place?.eventsMgr.removeListener("wspUriChange", this._onUriChangeLstn);
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoCurrentItem) {
			this.setCurrentItem(info.srcUri);
		} else if (info instanceof InfoHighlighItemSgn) {
			this._highlightSgn = info.sgnPattern;
			this._assigned = info.assigned;
			this.grid.invalidateRows();
		}
	}

	/** */
	redrawLine(row: IGridDataRow, line: HTMLElement): void {
		redrawSrcLine(this.wsp, row.rowKey, line, this._currentSrcUri, this._highlightSgn, this._assigned);
	}

	_currentSrcUri: srcUri;
	protected _highlightSgn: RegExp;
	protected _assigned: srcRef;

	buildLastDatas(parentLastDatas: JLastDatas) {
		if (this.facets?.buildLastDatas) {
			const ld = {};
			this.facets.buildLastDatas(ld);
			if (Object.keys(ld).length > 0) parentLastDatas[this.area.getId()] = ld;
		}
	}
}

REG.reg.registerSkin('wsp-navigator', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		position: relative;
	}

	c-grid {
		flex: 1;
		border: none;
		border-top: 1px solid var(--border-color);
		background-color: var(--row-bgcolor);
	}
`);

REG.reg.registerSkin('wsp-navigator/grid', 1, /* language=CSS */ `
	.highlight {
		background-color: var(--row-highlight-bgcolor);
  }

  .assigned {
	  background-color: var(--row-assigned-bgcolor);
  }

  /** Réservation de la largeur de background-image pour qu'il n'y ait pas de superposition avec les secondary-cols */
  :host([data-has-secondary-cols]) .line {
	  padding-inline-end: .8rem;
  }

  :host([data-has-secondary-cols]) #resizers {
	  padding-inline-end: .8rem;
  }

  .current {
	  background-position: right;
	  background-repeat: no-repeat;
	  background-image: var(--row-current-img-end);
	  background-color: var(--row-current-bgcolor);
  }

  .icon {
	  filter: var(--filter);
	}
`);

customElements.define('wsp-navigator', Navigator);


class NavigatorDatas extends SpaceTreeDataSearch {
	initNavDatas(nav: Navigator): this {
		this.hideItemFolderContent = true;
		this.initSpTrDataSearch(nav.facets, 30, 50);
		return this;
	}

	onUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | IWspXmlHouse | 'local' | 'server') {
		if (this.wsp.code !== msg.wspCd) return;
		const node = this.findRowKeyBySrcUri(msg.srcUri);
		if (node) {
			if (this.shouldHideEntry(msg, node)) {
				this.deleteRowKey(node);
			} else {
				this.refreshRowKey(node);
			}
		}
	}
}


export class NavigatorFacetBase implements INavigatorFacets {

	skin: string | string[];

	/** Balise select de la requete. */
	protected select: Element;

	/** 1er criterion de la requete (fils de la balise where). */
	protected itemsExp: SearchItems;

	/** A incrémenter à chaque chgt de facette. */
	stamp: number = 0;

	constructor(fields: ESrcField[]) {
		this.initSkin();
		this.select = JSX.asXml(() => <select>{fields.map((f) => JSX.asXml(() => <column dataKey={f}/>))}</select>);
		this.itemsExp = new SearchItems().excludeTildeRoot().excludeAirItems(false);
	}


	protected initSkin() {}

	getStartReq(max: number): string {
		this.select.setAttribute("max", max.toString());
		this.itemsExp.afterUri = null;
		this.itemsExp.beforeEndFolder = null;
		return this.buildReq();
	}

	getNextReq(afterSrcUri: srcUri, max: number): string {
		this.select.setAttribute("max", max.toString());
		this.itemsExp.afterUri = afterSrcUri;
		this.itemsExp.beforeEndFolder = null;
		return this.buildReq();
	}

	getReplaceFolderReq(folderUri: srcUri, max: number): string {
		this.select.setAttribute("max", max.toString());
		const path = folderUri + '/';
		this.itemsExp.afterUri = path;
		this.itemsExp.beforeEndFolder = path;
		return this.buildReq();
	}

	/** A surcharger pour construire les facettes. */
	buildUiFacets(nav: Navigator, lastDatas: JLastDatas | null) {
		this.initRoot(nav).appendChild(nav.navigatorBtns);
	}

	protected initRoot(nav: Navigator): Node & ParentNode {
		if (this.skin) {
			const root = nav.facetRoot;
			const reg = nav.reg;
			const sr = root.attachShadow(DOMSH.SHADOWDOM_INIT);
			if (Array.isArray(this.skin)) {
				for (const sk of this.skin) reg.installSkin(sk, sr);
			} else {
				reg.installSkin(this.skin, sr);
			}
			return sr;
		}
		return nav.facetRoot;
	}

	/** A surcharger pour construire les critèrions Xml issus des facettes. */
	protected getCriterions(): Element | Element[] | undefined {return undefined}

	protected buildReq() {
		return DOM.ser(JSX.asXml(() => <request>{this.select}
			<where xmlns:sc="http://www.utc.fr/ics/scenari/v3/core">{this.itemsExp.toDom()}{this.getCriterions()}</where>
		</request>));
	}

	/** A appeler suite à tout changement de widget pour relancer la requete. */
	protected onChange(this: HTMLInputElement, ev: Event) {
		const nav = DOMSH.findFlatParentElt(this, null, (elt): elt is Navigator => elt instanceof Navigator);
		(nav.facets as NavigatorFacetBase).stamp++;
		nav.datas.fetchSearchStart(true);
	}

	getStamp(): any {return this.stamp}

	isSameStamp(stamp: any): boolean {return stamp === this.stamp}
}

export class NavigatorFacetText extends NavigatorFacetBase {

	textInput: HTMLInputElement;

	protected forcedCriterions?: Element[];

	protected initSkin() {
		this.skin = "wsp-navigator/facetText";
	}

	buildUiFacets(nav: Navigator, lastDatas: JLastDatas | null) {
		this.initRoot(nav).append(
			<div class="h">{this.buildTextInput(nav, lastDatas)}{nav.navigatorBtns}</div>
		);
	}

	buildLastDatas(parentLastDatas: JLastDatas) {
		const txt = this.textInput?.value.trim();
		if (txt) (parentLastDatas as JLDFacetText).txt = txt;
	}

	/* Ajout de criterions **sans** visibilité UI **/
	addCriterions(crit: Element[]): this {
		if (!this.forcedCriterions) this.forcedCriterions = [];
		this.forcedCriterions.push(...crit);
		return this;
	}

	protected buildTextInput(nav: Navigator, lastDatas: JLDFacetText | null): HTMLElement {
		this.textInput = <input type="search" spellCheck="false" placeholder="Filtrer..." aria-label="Filtrer" onchange={this.onChange} oninput={this.onChange} value={lastDatas?.txt}/> as HTMLInputElement;
		return <label id="txt">{this.textInput}</label>;
	}

	protected getCriterions(): Element | Element[] | undefined {
		return this.forcedCriterions ? LANG.pushIfDefined([], ...this.forcedCriterions, this.getTextCriterion()) : this.getTextCriterion();
	}

	protected getTextCriterion(): Element | undefined {
		if (!this.textInput) return undefined;
		const v = this.textInput.value.trim().toLowerCase();
		if (!v) return undefined;
		return JSX.asXml(() => <exp type="ItemCodeOrTitle" regexp={`(?i).*${LANG.escape4RegexpFuzzy(v)}.*`}/>)
	}
}

interface JLDFacetText {
	txt?: string
}


REG.reg.registerSkin('wsp-navigator/facetText', 1, /* language=CSS */ `
	:host {
		display: flex;
		flex-direction: column;
	}

	:focus {
		outline: var(--focus-outline);
	}

	.v {
		display: flex;
		flex-direction: column;
	}

	.h {
		display: flex;
		flex-direction: row;
	}

	#txt {
		background: 0.1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg) var(--form-search-bgcolor);
		padding-inline-start: 1.2em;
		flex: 1 1 auto;
	}

	input[type=search] {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		padding: 2px;
		background: var(--form-bgcolor);
		color: var(--form-color);
		border: none;
		width: 100%;
		font-size: inherit;
	}
	input::placeholder {
		color: var(--fade-color);
		letter-spacing: 2px;
		font-size: .8em;
		font-style: italic;
	}
`);

export class NavigatorFacetItState extends NavigatorFacetText {

	showWarning: boolean
	showValid: boolean
	showTextFilter: boolean

	protected errorCheck: HTMLInputElement;
	protected warningCheck: HTMLInputElement;
	protected validCheck: HTMLInputElement;

	initFacet(showWarning: boolean, showValid: boolean, showTextFilter: boolean): this {
		this.showWarning = showWarning;
		this.showValid = showValid;
		this.showTextFilter = showTextFilter;
		return this;
	}

	protected initSkin() {
		this.skin = ["wsp-navigator/facetText", "wsp-navigator/facetItSate"];
	}

	buildUiFacets(nav: Navigator, lastDatas: JLastDatas | null) {
		const root = this.initRoot(nav);
		root.appendChild(<div id="itStatesCtn">{this.buildErrorInput(nav, lastDatas)}{nav.navigatorBtns}</div>);
		if (this.showTextFilter) root.appendChild(this.buildTextInput(nav, lastDatas));
	}

	buildLastDatas(parentLastDatas: JLastDatas) {
		super.buildLastDatas(parentLastDatas);
		if (!this.errorCheck.checked) (parentLastDatas as JLDFacetItSt).err = false;
		if (!this.warningCheck?.checked) (parentLastDatas as JLDFacetItSt).warn = false;
		if (this.validCheck?.checked) (parentLastDatas as JLDFacetItSt).valid = true;
	}

	protected buildErrorInput(nav: Navigator, lastDatas: JLDFacetItSt | null): HTMLElement | undefined {
		this.errorCheck = <input id="error" type="checkbox" onchange={this.onChange} checked={lastDatas?.err === false ? undefined : ""}/> as HTMLInputElement;
		if (!this.showWarning && !this.showValid) {
			this.errorCheck.checked = true;
			return <div id="itState"/>;
		}
		if (this.showWarning) this.warningCheck = <input id="warning" type="checkbox" onchange={this.onChange} checked={lastDatas?.warn === false ? undefined : ""}/> as HTMLInputElement;
		if (this.showValid) this.validCheck = <input id="valid" type="checkbox" onchange={this.onChange} checked={lastDatas?.valid ? "" : undefined}/> as HTMLInputElement;
		const ctn = <div id="itState"><label class="h" id="error">{this.errorCheck}Erreur</label></div>;
		if (this.warningCheck) ctn.appendChild(<label class="h" id="warning">{this.warningCheck}Alerte</label>);
		if (this.validCheck) ctn.appendChild(<label class="h" id="valid">{this.validCheck}Valide</label>);
		return ctn;
	}

	protected getCriterions(): Element | Element[] | undefined {
		let searchCrit: ISearchCrit;
		const min = this.validCheck?.checked ? 1 : this.warningCheck?.checked ? 2 : this.errorCheck.checked ? 3 : 99;
		if (min >= 3) {
			searchCrit = new SearchStatus(min, min);
		} else {
			const max = this.errorCheck.checked ? 3 : this.warningCheck?.checked ? 2 : 1;
			if (min === 1 && max === 3) {
				//cas particulier Error + Valid => exclure wanring
				searchCrit = new SearchOr(new SearchStatus(min, min), new SearchStatus(max, max));
			} else {
				searchCrit = new SearchStatus(min, max);
			}
		}
		return LANG.pushIfDefined([searchCrit.toDom()], this.getTextCriterion());
	}
}

interface JLDFacetItSt {
	err?: boolean
	warn?: boolean
	valid?: boolean
}


REG.reg.registerSkin('wsp-navigator/facetItSate', 1, /* language=CSS */ `
	#itStatesCtn, #itState {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
	}

	#itState {
		flex-wrap: wrap;
	}

	#itState > label {
		margin-inline-end: .5em;
		white-space: nowrap;
	}

	#error {
		color: var(--error-color);
	}

	#warning {
		color: var(--warning-color);
	}

	#valid {
		color: var(--valid-color);
	}

	#txt {
		flex: 0;
	}
`);

/** Tous les states error, warning, valid + text filter. */
export const FACET_itStates = (nav: Navigator, fields: ESrcField[]) => new NavigatorFacetItState(fields).initFacet(true, true, true);

/** Items en erreur ou warnings, sans text filter. */
export const FACET_itProblems = (nav: Navigator, fields: ESrcField[]) => new NavigatorFacetItState(fields).initFacet(true, false, false);

/** Items en erreur, sans text filter. */
export const FACET_itErrors = (nav: Navigator, fields: ESrcField[]) => new NavigatorFacetItState(fields).initFacet(false, false, false);